import { fillInWhitespacesInTranscriptElements } from '@src/editor/services/punctuation';
import { FILLER_WORDS } from '@src/videoTranscriptionProcessor/constants/karaokeConstants';

import { TextElement } from '../elements/TextElement';

import type { KaraokeConfig } from './KaraokeConfig';
import type { TranscriptTextToken, TranscriptToken } from '@src/editor';
import type { KaraokeLine } from '@src/editor/types/karaoke';

export const MINIMUM_KARAOKE_CLIP_DURATION = 0.1;

export class KaraokeClip extends TextElement {
  transcriptTokens: TranscriptToken[];
  hiddenTranscriptTokenIds: string[] = [];
  startSecondsOffset?: number;
  endSecondsOffset?: number;
  karaokeConfig: KaraokeConfig;
  ignoredByFiltersIds: string[] = [];

  constructor(
    props: Partial<KaraokeClip> & {
      transcriptTokens: TranscriptToken[];
      karaokeConfig: KaraokeConfig;
    },
  ) {
    super({ ...props, type: 'text' });
    // TODO: parse tokens for state: hidden, muted_by_hideFillers, muted_by_hidePunct and add to hiddenTranscriptTokenIds
    this.transcriptTokens = props.transcriptTokens;
    this.karaokeConfig = props.karaokeConfig;
    if (props.hiddenTranscriptTokenIds) {
      this.hiddenTranscriptTokenIds = props.hiddenTranscriptTokenIds;
    }
    if (props.startSecondsOffset != null) {
      this.startSecondsOffset = props.startSecondsOffset;
    }
    if (props.endSecondsOffset != null) {
      this.endSecondsOffset = props.endSecondsOffset;
    }
    if (props.ignoredByFiltersIds) {
      this.ignoredByFiltersIds = props.ignoredByFiltersIds;
    }
  }

  public setKaraokeConfig(config: KaraokeConfig) {
    this.karaokeConfig = config;
  }

  public setHiddenTranscriptTokenIds(ids: string[]): void {
    this.hiddenTranscriptTokenIds = ids;
  }

  public setIgnoredByFiltersIds(ids: string[]): void {
    this.ignoredByFiltersIds = ids;
  }

  public getAllHiddenTranscriptTokenIds(): string[] {
    return [
      ...new Set([
        ...this.hiddenTranscriptTokenIds,
        ...this.getHiddenByConfigIds(),
      ]),
    ];
  }

  public filterByKaraokeConfig(
    token: TranscriptToken,
    idx: number,
    allTokens: TranscriptToken[],
  ) {
    if (
      this.karaokeConfig.hideFillers &&
      token.type === 'punct' &&
      token.value === ',' &&
      this.isAdjacentToFillerWord(idx, allTokens)
    ) {
      return false; // Hide comma bordering a filler word destined to be hidden
    }

    if (
      this.karaokeConfig.hideFillers &&
      token.value &&
      this.hasFillerWords(token.value)
    ) {
      return false;
    }

    if (this.karaokeConfig.hideComma && token.value === ',') {
      return false;
    }
    if (this.karaokeConfig.hidePeriod && token.value === '.') {
      return false;
    }
    return true;
  }

  private isAdjacentToFillerWord(
    idx: number,
    allTokens: TranscriptToken[],
  ): boolean {
    const prevIdx = this.findPreviousNonWhitespaceTokenIndex(idx, allTokens);
    const nextIdx = this.findNextNonWhitespaceTokenIndex(idx, allTokens);

    const prev = prevIdx != null ? allTokens[prevIdx] : null;
    const next = nextIdx != null ? allTokens[nextIdx] : null;

    const isAdjacentToHiddenFiller = (t: TranscriptToken | null): boolean =>
      !!(
        t &&
        this.karaokeConfig.hideFillers &&
        t.value &&
        this.hasFillerWords(t.value) &&
        !this.ignoredByFiltersIds.includes(t.id)
      );

    return isAdjacentToHiddenFiller(prev) || isAdjacentToHiddenFiller(next);
  }

  private findPreviousNonWhitespaceTokenIndex(
    fromIdx: number,
    tokens: TranscriptToken[],
  ): number | null {
    for (let i = fromIdx - 1; i >= 0; i--) {
      const t = tokens[i];
      if (!(t.type === 'punct' && t.value === ' ')) {
        return i;
      }
    }
    return null;
  }

  private findNextNonWhitespaceTokenIndex(
    fromIdx: number,
    tokens: TranscriptToken[],
  ): number | null {
    for (let i = fromIdx + 1; i < tokens.length; i++) {
      const t = tokens[i];
      if (!(t.type === 'punct' && t.value === ' ')) {
        return i;
      }
    }
    return null;
  }

  private getHiddenByConfigIds(): string[] {
    return this.transcriptTokens
      .filter(
        (t, idx, all) =>
          // filtered out by config
          !this.filterByKaraokeConfig(t, idx, all) &&
          // not manually restored by user
          !this.ignoredByFiltersIds.includes(t.id),
      )
      .map(t => t.id);
  }

  private hasFillerWords(text: string): boolean {
    const fillerRegex = new RegExp(`\\b(?:${FILLER_WORDS.join('|')})\\b`, 'gi');
    return fillerRegex.test(text);
  }

  private getKaraokeText(): string {
    const allHiddenTokenIds = this.getAllHiddenTranscriptTokenIds();
    return fillInWhitespacesInTranscriptElements(
      this.transcriptTokens
        .filter(token => !allHiddenTokenIds.includes(token.id))
        .filter(token => token.value !== '\n'),
    )
      .map(token => token.value)
      .join('');
  }

  public getTranscriptStartTime(): number | null {
    const firstToken = this.transcriptTokens.find(
      (token): token is TranscriptTextToken =>
        'ts' in token && token.ts !== null,
    );
    if (!firstToken) return null;
    return firstToken.ts;
  }

  public getTranscriptEndTime(): number | null {
    const reversedTokens = [...this.transcriptTokens].reverse();
    const lastToken = reversedTokens.find(
      (token): token is TranscriptTextToken & { end_ts: number } =>
        'end_ts' in token && token.end_ts !== null,
    );
    const startTime = this.getKaraokeStartTime();
    if (!lastToken) return null;
    if (startTime == null) return null;
    return lastToken.end_ts;
  }

  public getKaraokeStartTime(): number | null {
    return (
      (this.startSecondsOffset || 0) + (this.getTranscriptStartTime() || 0)
    );
  }

  public getKaraokeDuration(): number | null {
    return (
      (this.endSecondsOffset || 0) +
      (this.getTranscriptEndTime() || 0) -
      (this.getKaraokeStartTime() || 0)
    );
  }

  public getKaraokeEndTime(): number | null {
    const start = this.getKaraokeStartTime();
    const duration = this.getKaraokeDuration();
    if (start == null || duration == null) return null;
    return start + duration;
  }

  /**
   * Converts transcript tokens to normalized timing for karaoke animation
   * - Shifts all word times by start offset
   * - Increases durations evenly so last word end time equals karaoke end time
   */
  private getTranscriptSourceForAnimation(): Array<{
    value: string;
    time: number;
    duration: number;
  }> {
    const allHiddenTokenIds = this.getAllHiddenTranscriptTokenIds();

    // append punctuation to previous text token value
    const tokens = this.transcriptTokens
      .filter(t => !allHiddenTokenIds.includes(t.id))
      .reduce((acc: TranscriptTextToken[], token) => {
        if (token.type === 'punct' && acc.length > 0) {
          const last = acc[acc.length - 1];
          last.value = last.value ? last.value + token.value : token.value;
        } else if (token.type === 'text') {
          acc.push({ ...token }); // avoid mutating original token
        }
        return acc;
      }, [])
      .filter(
        (t): t is TranscriptTextToken & { end_ts: number; ts: number } =>
          t.ts != null && t.end_ts != null,
      );

    const clipStartTime = this.getKaraokeStartTime();
    const clipEndTime = this.getKaraokeEndTime();

    if (tokens.length === 0 || clipStartTime == null || clipEndTime == null)
      return [];

    // Calculate the original transcript time range
    const originalStartTime = tokens[0].ts;
    const lastToken = tokens[tokens.length - 1];
    const originalEndTime = lastToken.end_ts;
    const originalDuration = originalEndTime - originalStartTime;

    // Calculate the target duration (karaoke duration)
    const targetDuration = clipEndTime - clipStartTime;

    // Calculate the scaling factor for durations
    const scaleFactor =
      originalDuration > 0 ? targetDuration / originalDuration : 1;

    return tokens.map(word => {
      // Shift time relative to clip start
      const relativeTime = word.ts - originalStartTime;
      const scaledTime = relativeTime * scaleFactor;

      // Scale the word duration
      const originalWordDuration = word.end_ts - word.ts;
      const scaledDuration = originalWordDuration * scaleFactor;

      return {
        value: word.value || '',
        time: scaledTime,
        duration: scaledDuration,
      };
    });
  }

  // We can create a curve of pace of words in transcript to better approximate
  // animiation of text!  Thanks ChatGPT o1-preview!
  // private calculateCubicBezierForWords(): number[] {
  //   if (this.transcriptTokens.length <= 1 || this.startTs >= this.endTs) {
  //     return [0, 0, 1, 1]; // Default linear cubic-bezier
  //   }

  //   // Normalize word timings and calculate cumulative progress
  //   const totalDuration = this.endTs - this.startTs;

  //   // Function to compute cumulative progress at normalized time t
  //   const computeCumulativeProgressAt = (t: number): number => {
  //     let cumulativeProgress = 0;

  //     for (const word of this.words) {
  //       const wordStart = (word.relativeStart - this.startTs) / totalDuration;
  //       const wordEnd = (word.relativeEnd - this.startTs) / totalDuration;

  //       if (wordEnd <= t) {
  //         // Word has ended before time t, include full duration
  //         cumulativeProgress += wordEnd - wordStart;
  //       } else if (wordStart >= t) {
  //         // Word starts after time t, do not include
  //         continue;
  //       } else {
  //         // Word is in progress at time t, include partial duration
  //         cumulativeProgress += t - wordStart;
  //       }
  //     }

  //     return cumulativeProgress; // Already normalized
  //   };

  //   // Choose t1 and t2 for solving the Bezier control points
  //   const t1 = 0.3;
  //   const t2 = 0.6;

  //   // Compute cumulative progress at t1 and t2
  //   const p1 = computeCumulativeProgressAt(t1);
  //   const p2 = computeCumulativeProgressAt(t2);

  //   // Coefficients for the Bezier curve equations
  //   const a1 = 3 * (1 - t1) * (1 - t1) * t1;
  //   const b1 = 3 * (1 - t1) * t1 * t1;
  //   const c1 = t1 * t1 * t1;

  //   const a2 = 3 * (1 - t2) * (1 - t2) * t2;
  //   const b2 = 3 * (1 - t2) * t2 * t2;
  //   const c2 = t2 * t2 * t2;

  //   // Right-hand sides of the equations
  //   const rhs1 = p1 - c1;
  //   const rhs2 = p2 - c2;

  //   // Solve the linear system to find y1 and y2
  //   const denominator = a1 * b2 - a2 * b1;
  //   if (denominator === 0) {
  //     // Fallback to linear if system cannot be solved
  //     return [0, 0, 1, 1];
  //   }

  //   const y1 = (rhs1 * b2 - rhs2 * b1) / denominator;
  //   const y2 = (a1 * rhs2 - a2 * rhs1) / denominator;

  //   // Ensure control points are within [0,1]
  //   const y1Clamped = Math.min(Math.max(y1, 0), 1);
  //   const y2Clamped = Math.min(Math.max(y2, 0), 1);

  //   // Set x1 and x2 equal to t1 and t2
  //   const x1 = t1;
  //   const x2 = t2;

  //   return [x1, y1Clamped, x2, y2Clamped];
  // }

  toKaraokeLine(): KaraokeLine {
    return {
      id: this.id,
      transcriptTokens: this.transcriptTokens,
      hiddenTranscriptTokenIds: this.hiddenTranscriptTokenIds,
      ignoredByFiltersIds: this.ignoredByFiltersIds,
      allHiddenTranscriptTokenIds: this.getAllHiddenTranscriptTokenIds(),
      transcriptStartTime: this.getTranscriptStartTime() || 0,
      transcriptEndTime: this.getTranscriptEndTime() || 0,
      startSecondsOffset: this.startSecondsOffset,
      endSecondsOffset: this.endSecondsOffset,
      karaokeConfigId: this.karaokeConfig.id,
    };
  }

  /**
   * Converts the element to JSON compatible with Creatomate
   */
  toJSON(formatTime: Boolean = false): Record<string, any> {
    const _config = this.karaokeConfig;
    const {
      shadow_color,
      shadow_blur,
      shadow_x,
      shadow_y,
      transcript_color,
      transcript_effect,
      transcript_maximum_length,
      transcript_placement,
      transcript_split,
      ...config
    } = _config;
    const time = this.getKaraokeStartTime();
    const duration = this.getKaraokeDuration();

    // Params to improve timing of word appearance.
    const bezierParams = '0,0,1,1'; // Linear

    const transcriptSource = this.getTranscriptSourceForAnimation();

    const json = {
      ...super.toJSON(formatTime),
      text: this.getKaraokeText(),
      time,
      duration,
      ...config,
      id: this.id, // to avoid override from config
      z_index: 1000, // Ensure karaoke elements appear above layout compositions

      ...(shadow_color && {
        shadow_color,
        shadow_blur,
        shadow_x,
        shadow_y,
      }),

      animations: config.animations.map((anim: any) => ({
        ...anim,
        duration: Math.max(0.1, duration! - 0.2),
        fade: false,
        easing: `cubic-bezier(${bezierParams})`,
        background_effect: 'animated',
      })),

      ...(transcript_effect &&
        transcriptSource.length > 0 && {
          transcript_effect,
          transcript_split,
          transcript_placement,
          transcript_maximum_length,
          transcript_color,
          transcript_source: this.getTranscriptSourceForAnimation(),
        }),
    };

    return json;
  }
}
